( () => {
  /* =======================
   * Config
   * ======================= */
  const ENTER = 1; // add .scrolled after this
  const EXIT = 0; // remove .scrolled when back above this
  const NAV_SELECTOR = 'header .e-div--header-wrapper .wp-block-navigation';
  const CSS_VAR_NAME = '--topNavH';

  /* =======================
   * State
   * ======================= */
  const state = {
    isScrolled: false,
    lastWidth: window.innerWidth,
    needsRecalc: false, // set when resized while scrolled; recompute once back at top
    rafId: null,
    pendingY: 0,
  };

  /* =======================
   * Utils
   * ======================= */
  const atTop = ( y ) => y <= EXIT;

  const debounce = ( fn, wait = 120 ) => {
    let t;
    return ( ...args ) => { clearTimeout( t ); t = setTimeout( () => fn( ...args ), wait ); };
  };

  const setCssVar = ( name, value ) => {
    document.documentElement.style.setProperty( name, value );
  };

  /* =======================
   * Measuring (off-DOM clone, no flicker)
   * ======================= */
  const queryNav = () => document.querySelector( NAV_SELECTOR );

  const measureNavHeight = () => {
    const nav = queryNav();
    if ( !nav ) return 0;

    const clone = nav.cloneNode( true );
    Object.assign( clone.style, {
      position: 'absolute',
      top: '-9999px',
      left: '0',
      visibility: 'hidden',
      maxHeight: 'none',
      display: 'block',
      transition: 'none',
      pointerEvents: 'none',
    } );

    document.body.appendChild( clone );
    const h = Math.max( clone.scrollHeight, clone.offsetHeight, 0 );
    clone.remove();
    return h;
  };

  const setNavHeightVar = () => {
    const h = measureNavHeight();
    if ( h > 0 ) setCssVar( CSS_VAR_NAME, `${h}px` );
  };

  /* =======================
   * Scrolled state (hysteresis)
   * ======================= */
  const addScrolled = () => {
    document.body.classList.add( 'scrolled' );
    state.isScrolled = true;
  };

  const removeScrolled = () => {
    document.body.classList.remove( 'scrolled' );
    state.isScrolled = false;
  };

  const updateScrolledState = ( y ) => {
    if ( !state.isScrolled && y > ENTER ) {
      addScrolled();
    } else if ( state.isScrolled && y <= EXIT ) {
      removeScrolled();
    }
  };

  /* =======================
   * Height recalc deferral
   * ======================= */
  const ensureNavHeightIfNeeded = ( y ) => {
    if ( atTop( y ) ) {
      setNavHeightVar();
      state.needsRecalc = false;
      return;
    }
    state.needsRecalc = true;
  };

  const maybeDoDeferredRecalc = ( y ) => {
    if ( state.needsRecalc && y <= EXIT ) {
      setNavHeightVar();
      state.needsRecalc = false;
    }
  };

  /* =======================
   * Scroll (batched via rAF)
   * ======================= */
  const flushScroll = () => {
    const y = state.pendingY;
    maybeDoDeferredRecalc( y );
    updateScrolledState( y );
    state.rafId = null;
  };

  const onScroll = () => {
    state.pendingY = window.scrollY || 0;
    if ( state.rafId ) return;
    state.rafId = requestAnimationFrame( flushScroll );
  };

  /* =======================
   * Resize (horizontal only)
   * ======================= */
  const onResize = () => {
    const w = window.innerWidth;
    if ( w === state.lastWidth ) return; // ignore vertical viewport UI changes
    state.lastWidth = w;

    ensureNavHeightIfNeeded( window.scrollY || 0 );
    onScroll(); // re-evaluate scrolled state after potential layout change
  };

  /* =======================
   * DOM mutations (optional)
   * ======================= */
  const observeHeaderMutations = () => {
    const header = document.querySelector( 'header' );
    if ( !header || !( 'MutationObserver' in window ) ) return;

    const mo = new MutationObserver(
      debounce( () => ensureNavHeightIfNeeded( window.scrollY || 0 ), 100 )
    );
    mo.observe( header, { childList: true, subtree: true } );
  };

  /* =======================
   * Init
   * ======================= */
  const init = () => {
    // Initial measure at top (prevents layout jump on load)
    setNavHeightVar();
    // Apply scrolled state if page loads mid-scroll (e.g., with anchor)
    onScroll();

    window.addEventListener( 'scroll', onScroll, { passive: true } );
    window.addEventListener( 'resize', debounce( onResize, 140 ) );
    observeHeaderMutations();
  };

  document.addEventListener( 'DOMContentLoaded', init );
} )();
